/**
 * `@dxf-ai-editor/acadctb` — binary parser for AutoCAD plot-style
 * tables (`.ctb` color-dependent and `.stb` named).
 *
 * Container layout:
 *
 *   ```
 *   "PIAFILEVERSION_2.0,CTBVER1,compress\r\npmzlibcodec\r\n"
 *   <4 bytes: uncompressed size, little-endian uint32>
 *   <zlib-deflate stream of ASCII body>
 *   ```
 *
 * (`.stb` swaps `CTBVER1` for `STBVER1`.)
 *
 * After inflation the body is a hierarchical ASCII-tagged tree
 * (see `./lexer.ts`) describing top-level metadata, a `plot_style`
 * array of per-entry blocks, and an optional `custom_lineweight_table`
 * array of mm values indexed by `lineweightIndex`.
 *
 * Reference: this module ports the parsing logic of
 * `ezdxf/src/ezdxf/acadctb.py` (MIT licensed; semantics only,
 * not source verbatim). Consumer wiring is delivered by PDF-6.3b.
 */

import { inflate } from "pako";

import { buildEntries, buildLineweightTable } from "./entries";
import { makeError } from "./errors";
import {
  parseBool,
  parseFloatField,
  parseIntField,
  parseStringField,
} from "./fields";
import { findBlock, findLeaf, parseTagTree } from "./lexer";
import type { PlotStyleTable, TableKind } from "./types";

const HEADER_MAGIC = "PIAFILEVERSION_2.0";
const CTB_TOKEN = "CTBVER1";
const STB_TOKEN = "STBVER1";
const HEADER_SENTINEL = "pmzlibcodec\r\n";

/* --------------------------------------------------------------- *\
 * Header sniff + decode
\* --------------------------------------------------------------- */

/**
 * Quick sniff: returns `true` when the buffer begins with the
 * AutoCAD plot-style magic. Does not validate the rest of the
 * header.
 */
export function isAcadctbBuffer(bytes: Uint8Array): boolean {
  if (bytes.length < HEADER_MAGIC.length) {
    return false;
  }
  for (let i = 0; i < HEADER_MAGIC.length; i += 1) {
    if (bytes[i] !== HEADER_MAGIC.charCodeAt(i)) {
      return false;
    }
  }
  return true;
}

export interface AcadctbHeader {
  kind: TableKind;
  uncompressedSize: number;
  /** Byte offset where the zlib-deflate stream begins. */
  payloadOffset: number;
}

/**
 * Parse the ASCII header line + 4-byte uncompressed-size word.
 *
 * The header is terminated by `pmzlibcodec\r\n`; the 4-byte
 * little-endian size immediately follows.
 */
export function decodeAcadctbHeader(bytes: Uint8Array): AcadctbHeader {
  if (!isAcadctbBuffer(bytes)) {
    throw makeError("magic", "buffer does not start with PIAFILEVERSION_2.0");
  }
  const sentinelOffset = indexOfAscii(bytes, HEADER_SENTINEL, 0);
  if (sentinelOffset < 0) {
    throw makeError("magic", "missing pmzlibcodec sentinel in header");
  }
  const headerAscii = asciiSlice(bytes, 0, sentinelOffset);
  let kind: TableKind | null = null;
  if (headerAscii.includes(CTB_TOKEN)) {
    kind = "ctb";
  } else if (headerAscii.includes(STB_TOKEN)) {
    kind = "stb";
  }
  if (kind === null) {
    throw makeError(
      "magic",
      "header version token not recognised (expected CTBVER1 or STBVER1)",
    );
  }
  const sizeOffset = sentinelOffset + HEADER_SENTINEL.length;
  if (sizeOffset + 4 > bytes.length) {
    throw makeError("magic", "buffer truncated before uncompressed-size word");
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset + sizeOffset, 4);
  const uncompressedSize = view.getUint32(0, /* littleEndian */ true);
  return { kind, uncompressedSize, payloadOffset: sizeOffset + 4 };
}

function indexOfAscii(
  bytes: Uint8Array,
  needle: string,
  from: number,
): number {
  const limit = bytes.length - needle.length;
  outer: for (let i = from; i <= limit; i += 1) {
    for (let j = 0; j < needle.length; j += 1) {
      if (bytes[i + j] !== needle.charCodeAt(j)) {
        continue outer;
      }
    }
    return i;
  }
  return -1;
}

function asciiSlice(bytes: Uint8Array, start: number, end: number): string {
  let out = "";
  for (let i = start; i < end; i += 1) {
    out += String.fromCharCode(bytes[i] ?? 0);
  }
  return out;
}

/* --------------------------------------------------------------- *\
 * Body inflate
\* --------------------------------------------------------------- */

function inflateBody(
  bytes: Uint8Array,
  payloadOffset: number,
  uncompressedSize: number,
): string {
  let raw: Uint8Array;
  try {
    raw = inflate(bytes.subarray(payloadOffset));
  } catch (cause) {
    throw makeError(
      "decompress",
      `zlib inflate failed: ${(cause as Error)?.message ?? String(cause)}`,
    );
  }
  if (uncompressedSize > 0 && raw.length !== uncompressedSize) {
    // Not fatal — AutoCAD occasionally pads — but we surface the
    // discrepancy as a `field` error if the diff is large enough to
    // suggest corruption. <= 16-byte slack is tolerated.
    if (Math.abs(raw.length - uncompressedSize) > 16) {
      throw makeError(
        "field",
        `inflated body length ${raw.length} disagrees with header size ${uncompressedSize}`,
      );
    }
  }
  return asciiSlice(raw, 0, raw.length);
}

/* --------------------------------------------------------------- *\
 * Public entry
\* --------------------------------------------------------------- */

/**
 * Parse a complete `.ctb` or `.stb` file into a {@link PlotStyleTable}.
 *
 * Throws {@link ParseAcadctbError} (a plain `Error` carrying a
 * structured `code`) on malformed input. The parser is intentionally
 * defensive about missing optional fields — AutoCAD's writer omits
 * defaults — but is strict about structural errors (unbalanced
 * blocks, wrong entry count, missing `Normal` on STB).
 */
export function parseAcadctb(bytes: Uint8Array): PlotStyleTable {
  const header = decodeAcadctbHeader(bytes);
  const body = inflateBody(bytes, header.payloadOffset, header.uncompressedSize);
  const tree = parseTagTree(body);

  const description = parseStringField(findLeaf(tree, "description"), "");
  const aciTableAvailable =
    header.kind === "ctb"
      ? parseBool(findLeaf(tree, "aci_table_available"), true)
      : false;
  const scaleFactor = parseFloatField(
    findLeaf(tree, "scale_factor"),
    1.0,
    "scale_factor",
  );
  const applyFactor = parseBool(findLeaf(tree, "apply_factor"), true);
  const customLineweightDisplayUnits = parseIntField(
    findLeaf(tree, "custom_lineweight_display_units"),
    0,
    "custom_lineweight_display_units",
  );
  const customLineweights = buildLineweightTable(
    findBlock(tree, "custom_lineweight_table"),
  );
  const entries = buildEntries(findBlock(tree, "plot_style"), header.kind);

  return {
    kind: header.kind,
    description,
    aciTableAvailable,
    scaleFactor,
    applyFactor,
    customLineweightDisplayUnits,
    customLineweights,
    entries,
  };
}
